// Entry helpers for the Weight "Record patient BMI" step only. BMI is still calculated by BMICaptureNode.

export const HEIGHT_CM_RANGE = { min: 100, max: 250 } as const;
export const HEIGHT_IN_RANGE = { min: 39, max: 98 } as const; // 3 ft 3 in to 8 ft 2 in
export const WEIGHT_KG_RANGE = { min: 30, max: 350 } as const;
export const WEIGHT_LB_RANGE = { min: 66, max: 770 } as const;

export type MeasurementSource = "measured" | "patient-reported" | "record";

export const MEASUREMENT_SOURCE_LABELS: Record<MeasurementSource, string> = {
  measured: "Measured in pharmacy today",
  "patient-reported": "Patient-reported",
  record: "From patient record",
};

export const MEASUREMENT_SOURCES = Object.keys(MEASUREMENT_SOURCE_LABELS) as MeasurementSource[];

export const fmt = (n: number, dp = 1) => String(Number(n.toFixed(dp)));

export const measurementLooksWrong = (what: string, shown: string) =>
  `${what} ${shown} looks wrong – check the number and units`;

export function inRange(value: number, range: { min: number; max: number }): boolean {
  return value >= range.min - 1e-9 && value <= range.max + 1e-9;
}

export type InclusionState = "meets" | "comorbidity" | "below";
export interface InclusionResult {
  state: InclusionState;
  text: string;
  detail?: string;
}

/**
 * Mirrors the thresholds the shared assessment applies next (>=30; >=27.5 adjusted group; <27 below; otherwise the
 * comorbidity question). It only describes the result; routing stays in the pathway.
 */
export function inclusionResult(bmi: number, adjustedGroupLabel?: string): InclusionResult {
  const shown = bmi.toFixed(1);
  const threshold = adjustedGroupLabel ? 27.5 : 30;
  if (bmi >= threshold) {
    if (adjustedGroupLabel && bmi < 30) {
      return {
        state: "meets",
        text: `Treated as BMI ≥30 for tirzepatide, semaglutide and liraglutide (${adjustedGroupLabel} – adjusted threshold). Mysimba and orlistat use standard thresholds.`,
      };
    }
    return { state: "meets", text: "BMI ≥30 – meets the BMI threshold for all medicines under this service." };
  }
  if (bmi >= 27) {
    return {
      state: "comorbidity",
      text: `BMI ${shown} is between 27 and ${threshold} – meets the threshold only with at least one weight-related comorbidity (asked next)`,
    };
  }
  return {
    state: "below",
    text: `Below inclusion threshold – BMI ${shown} < 27. Not eligible under this PGD.`,
    detail: "Do not proceed. Discuss reason · offer lifestyle advice · refer to GP if appropriate · document.",
  };
}

export function lastRecordedLine(rec?: { heightCm?: number; weightKg?: number; recordedAt?: string }): string {
  const parts = [
    rec?.heightCm !== undefined ? `${fmt(rec.heightCm)} cm` : "",
    rec?.weightKg !== undefined ? `${fmt(rec.weightKg)} kg` : "",
    rec?.recordedAt && !Number.isNaN(Date.parse(rec.recordedAt))
      ? new Date(rec.recordedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
      : "",
  ].filter(Boolean);
  return rec?.heightCm === undefined && rec?.weightKg === undefined ? "No height or weight on record" : `Last recorded: ${parts.join(", ")}`;
}
